<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Chip from 'primevue/chip';
import { useRelayHubStore } from '@/store';
import { EMIT } from '@/enum';
import { useArrayUtilities } from '@/composables';
import ConnectOrganizationModal from './ConnectOrganizationModal.vue';
import EditOrganizationModal from './EditOrganizationModal.vue';
import { TreeNode } from 'primevue/treenode';

const relayHubStore = useRelayHubStore();

const showAddOrganizationModal = ref(false);
const showEditOrganizationModal = ref(false);
const { connectedOrganizations, selectedOrganizationIds, originalConnectedOrganizationIds } =
  storeToRefs(relayHubStore);

const selectedOrganization = ref<TreeNode | null>(null);

const emit = defineEmits<{
  (e: EMIT.RESET): void;
}>();

const openEditOrganization = () => {
  showEditOrganizationModal.value = true;
};

const closeEditOrganization = () => {
  showEditOrganizationModal.value = false;
};

const openAddOrganizations = () => {
  showAddOrganizationModal.value = true;
};

const closeAddOrganizations = () => {
  showAddOrganizationModal.value = false;
};

const handleOrganizationConnected = () => {
  closeAddOrganizations();
  emit(EMIT.RESET);
};

const onCancel = async () => {
  emit(EMIT.RESET);
};

const handleEdit = (item: TreeNode) => {
  selectedOrganization.value = item;
  openEditOrganization();
};

const handleDelete = async (id: number) => {
  await relayHubStore.disableMedicaid([id], 'organizations');
  if (relayHubStore.error) relayHubStore.doToast(relayHubStore.error as string, 'Error', true);
  else relayHubStore.doToast('Organization Deleted');
  emit(EMIT.RESET);
};

const onSave = async () => {
  await relayHubStore.saveOrganizationMedicareAssignments();
  if (relayHubStore.error) relayHubStore.doToast(relayHubStore.error as string, 'Error', true);
  else relayHubStore.doToast('Organization Changes Saved');

  emit(EMIT.RESET);
};

const createMenuModel = (item: TreeNode) => {
  return [
    {
      label: 'Edit',
      command: () => handleEdit(item),
    },
    {
      label: 'Delete',
      command: () => handleDelete(item.id),
    },
  ];
};

const shouldDisable = computed(() =>
  useArrayUtilities().twoNumericArraysContainSameData(
    selectedOrganizationIds.value,
    originalConnectedOrganizationIds.value,
  ),
);

const handleOrganizationUpdated = () => {
  closeEditOrganization();
};

const organizationCount = computed(() => connectedOrganizations.value?.length);
</script>

<template>
  <Card>
    <template #header>
      <h2>Organizations</h2>
      <Chip>{{ organizationCount }}</Chip>

      <div class="ml-auto">
        <AppButton type="text" size="small" text="Add Organization" icon-left="add" @click="openAddOrganizations" />
      </div>
    </template>
    <template #content>
      <ConnectOrganizationModal
        v-if="showAddOrganizationModal"
        @cancel="closeAddOrganizations"
        @save="handleOrganizationConnected"
      />
      <EditOrganizationModal
        v-if="showEditOrganizationModal"
        :organization="selectedOrganization"
        @cancel="closeEditOrganization"
        @save="handleOrganizationUpdated"
      />
      <DataTable :value="connectedOrganizations" :loading="relayHubStore.loading">
        <Column header-style="width: 4em" class="text-center">
          <template #body="slotProps">
            <Checkbox v-model="selectedOrganizationIds" :name="slotProps.data.name" :value="slotProps.data.id" />
          </template>
        </Column>
        <Column field="name" header="Organization" />
        <Column field="medicaid_district_code" header="Medicaid District Code">
          <template #body="{ data }">
            <span>{{ data.medicaid_district_code || 'N/A' }}</span>
          </template>
        </Column>

        <Column field="actions" header="Actions" header-style="width: 4em" class="text-center">
          <template #body="{ data }">
            <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
          </template>
        </Column>
        <template #empty>There are no Organizations connected</template>
      </DataTable>
    </template>
    <template #footer>
      <AppButton text="Cancel" type="secondary" :disabled="shouldDisable" @click="onCancel" />
      <AppButton text="Save" type="primary" :disabled="shouldDisable" @click="onSave" />
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.p-card-header) {
  padding-right: 0px;
}
</style>
